import React from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { TableSkeleton } from "../../ui/Skeleton";
import Pagination from "./Pagination";
import Notification from "../Notification";

/**
 * Universal DataTable Component
 * Supports desktop table view and mobile card view with messages and pagination
 */
const DataTable = ({
  data,
  columns,
  renderMobileCard,
  loading = false,
  actionLoading = false,
  error,
  successMessage,
  onClearError,
  onClearSuccess,
  pagination,
  onRowClick,
  emptyState,
  className = "",
}) => {
  const { colors } = useTheme();

  return (
    <div className={`flex flex-col flex-1 md:overflow-hidden ${className}`}>
      {/* Messages Section - only show if there are messages */}
      {(successMessage || error || actionLoading) && (
        <div className="p-6 flex-shrink-0">
          {successMessage && (
            <Notification
              type="success"
              title={
                typeof successMessage === "object" ? successMessage.title : null
              }
              message={
                typeof successMessage === "string"
                  ? successMessage
                  : successMessage.message
              }
              extraInfo={
                typeof successMessage === "object"
                  ? successMessage.extraInfo
                  : null
              }
              warnings={
                typeof successMessage === "object"
                  ? successMessage.warnings
                  : []
              }
              onClose={onClearSuccess}
            />
          )}

          {error && (
            <Notification
              type="error"
              title={typeof error === "object" ? error.title : "Error"}
              message={typeof error === "string" ? error : error.message}
              onClose={onClearError}
            />
          )}

          {actionLoading && (
            <Notification type="info" message="Processing operation..." />
          )}
        </div>
      )}

      {/* Table Container - Scrollable */}
      <div
        className={`flex-1 md:overflow-y-auto overflow-x-hidden min-h-0 md:custom-scrollbar pb-6`}
      >
        {loading ? (
          <TableSkeleton rows={pagination?.rowsPerPage || 10} columns={columns.length} />
        ) : data.length === 0 ? (
          <div className="text-center py-20">
            {emptyState?.icon && (
              <div
                className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center`}
              >
                {React.createElement(emptyState.icon, {
                  className: `w-8 h-8 ${colors.emptyStateIcon}`,
                })}
              </div>
            )}
            <h3 className={`text-lg font-bold ${colors.textPrimary} mb-2 tracking-tight`}>
              {emptyState?.title || "No data found"}
            </h3>
            <p className={`${colors.textTertiary} mb-6 max-w-sm mx-auto`}>
              {emptyState?.message || ""}
            </p>
            {emptyState?.action && (
              <button
                onClick={emptyState.action.onClick}
                className="inline-flex items-center space-x-2 px-6 py-2.5 bg-violet-600 text-white rounded-xl hover:bg-violet-700 transition-all duration-200 shadow-md font-bold text-sm"
              >
                {emptyState.action.icon &&
                  React.createElement(emptyState.action.icon, {
                    className: "w-4 h-4",
                  })}
                <span>{emptyState.action.label}</span>
              </button>
            )}
          </div>
        ) : (
          <div className="w-full">
            {/* Desktop Table Header - Sticky */}
            <div
              className={`hidden md:grid gap-4 px-6 py-4 ${colors.tableHeader} border-b ${colors.tableBorder} text-xs uppercase tracking-wider font-bold ${colors.textTertiary} sticky top-0 z-20`}
              style={{
                gridTemplateColumns: columns
                  .map((col) => col.width || "1fr")
                  .join(" "),
              }}
            >
              {columns.map((column, index) => (
                <div
                  key={column.key || index}
                  // min-w-0 lets the grid item shrink below content size so
                  // wide labels (e.g. KAYDEDEN, AÇIKLAMA) don't push the
                  // following columns out of alignment on narrow screens.
                  className={`min-w-0 truncate ${column.key === "actions" ? "text-right pr-4" : ""}`}
                  title={typeof column.label === "string" ? column.label : undefined}
                >
                  {column.label}
                </div>
              ))}
            </div>

            {/* Desktop View - Systematic Table Body */}
            <div className="hidden md:flex flex-col">
              {data.map((row, rowIndex) => (
                <div
                  key={row.id || rowIndex}
                  data-row-id={row.id}
                  className={`
                    grid gap-4 px-6 py-4
                    ${colors.textPrimary} border-b ${colors.tableBorder}
                    transition-all duration-150 items-center
                    relative group hover:bg-white/[0.02]
                    ${onRowClick ? "cursor-pointer" : ""}
                  `}
                  style={{
                    gridTemplateColumns: columns
                      .map((col) => col.width || "1fr")
                      .join(" "),
                  }}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((column, colIndex) => {
                    const rawValue = row[column.key];
                    const titleAttr =
                      typeof rawValue === "string" || typeof rawValue === "number"
                        ? String(rawValue)
                        : undefined;
                    return (
                      <div
                        key={column.key || colIndex}
                        // min-w-0 + overflow-hidden are the magic combo that
                        // makes `truncate` actually work inside a CSS grid
                        // cell. Without them, long values overflow the cell
                        // and shift sibling columns visually.
                        className={`flex items-center text-sm font-medium min-w-0 overflow-hidden ${column.key === "actions" ? "justify-end" : ""}`}
                        title={titleAttr}
                      >
                        {column.render ? (
                          column.render(row)
                        ) : (
                          <span className={`truncate ${colors.textPrimary}`}>{rawValue}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Mobile View - card layout */}
            <div className="md:hidden space-y-3 pt-4">
              {data.map((row, index) => (
                <div key={row.id || index} data-row-id={row.id}>
                  {renderMobileCard ? (
                    renderMobileCard(row)
                  ) : (
                    <div
                      className={`px-4 py-3 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-white/5 ${colors.textPrimary} rounded-2xl shadow-sm`}
                    >
                      {columns.map((column, colIndex) => {
                        // Long-text columns (description / notes) get a full
                        // row instead of being squeezed into the right side.
                        const isLong =
                          column.key === "description" ||
                          column.label === "AÇIKLAMA" ||
                          column.label === "DESCRIPTION" ||
                          column.label === "NOTLAR";
                        const rawValue = row[column.key];
                        return (
                          <div
                            key={column.key || colIndex}
                            className={`py-2 border-b border-zinc-100 dark:border-white/5 last:border-0 ${
                              isLong ? "flex flex-col gap-1" : "flex justify-between items-center gap-3"
                            }`}
                          >
                            <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-wider shrink-0">
                              {column.label}
                            </span>
                            <div
                              className={`text-sm font-medium min-w-0 ${
                                isLong
                                  ? "text-left break-words"
                                  : "text-right truncate"
                              }`}
                              title={
                                typeof rawValue === "string" || typeof rawValue === "number"
                                  ? String(rawValue)
                                  : undefined
                              }
                            >
                              {column.render ? column.render(row) : <span>{rawValue}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pagination Footer - Always Visible at Bottom */}
      {!loading && data.length > 0 && pagination && (
        <div className="flex-shrink-0">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            rowsPerPage={pagination.rowsPerPage}
            totalItems={pagination.totalItems}
            startIndex={pagination.startIndex}
            onPageChange={pagination.onPageChange}
            onRowsPerPageChange={pagination.onRowsPerPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
